import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import moment from 'moment';
import AnimateHeight from 'react-animate-height';
import { v4 as uuidv4 } from 'uuid';
import ControlPointDuplicateIcon from '@mui/icons-material/ControlPointDuplicate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CircularProgress from '@mui/material/CircularProgress';

import {
  Autocomplete,
  Button,
  InputAdornment,
  TextField,
  IconButton
} from '@mui/material';

import ErrorIcon from '@mui/icons-material/Error';

import { getUserName } from '../utils/auth';
import { AppDataContext } from '../contexts';
import useFetch from '../hooks/useFetch';
import './AddCompanyBlock.scss';

export default function AddCompanyBlock({
  projectCompanies,
  setProjectCompanies,
  company,
  index,
  companyNameErrorList,
  setCompanyNameErrorList,
  portfolioCompaniesNames,
  investor,
  setCompanyRowAdded,
  companyListRef,
  companiesInProject,
  investorCompanyId,
  enterpriseCompanyId,
  projectId
}) {
  const [companyName, setCompanyName] = useState(company.companyName || '');
  const [note, setNote] = useState(company?.note || '');
  const [checkingClone, setCheckingClone] = useState(false);
  const [isIconActive, setIsIconActive] = useState(company.clone || false);
  const [existsInOtherProjects, setExistsInOtherProjects] = useState(company.existsElsewhere || false);
  const [selectedMatchDetails, setSelectedMatchDetails] = useState(null);

  const { enterpriseProjects, completedProjects } = useContext(AppDataContext);
  const [, getInvestorProjectsRequest] = useFetch();



  useEffect(() => {
    const copyCompanyNameErrorList = [...companyNameErrorList];
    if (companyNameErrorList[index] === true) {
      if (companyName.length > 0) {
        copyCompanyNameErrorList[index] = false;
        setCompanyNameErrorList(copyCompanyNameErrorList);
      }
    }
    if (companyNameErrorList.every((error) => error === false)) {
      setCompanyNameErrorList(copyCompanyNameErrorList);
    }
  }, [companyNameErrorList, companyName, projectCompanies]);

  const updateCompany = (updatedCompany) => {
    setProjectCompanies(prevCompanies => {
      const updatedCompanies = [...prevCompanies];
      // Only update if the company still exists at this index
      if (updatedCompanies[index] && updatedCompanies[index].companyId === company.companyId) {
        updatedCompanies[index] = updatedCompany;
      }
      return updatedCompanies;
    });
  };

  const handleCompanyNameChange = (value) => {
    const limitedValue = (value || '').slice(0, 100);
    const updatedCompany = { ...company, companyName: limitedValue };
    setCompanyName(limitedValue);
    updateCompany(updatedCompany);
  };

  const handleCompanyNameBlur = async () => {
    if (!companyName || !enterpriseProjects || !investorCompanyId || !enterpriseCompanyId || !projectId) return;

    setCheckingClone(true);
    
    try {
      // Combine both active enterprise projects and completed projects for comprehensive search
      const allProjects = [
        ...(enterpriseProjects || []),
        ...(completedProjects || [])
      ];

      const investorProjects = allProjects.filter(
        (p) => p.investorCompanyId === investorCompanyId && p.projectId !== projectId
      );

      console.log(`Searching in ${investorProjects.length} projects (${enterpriseProjects?.length || 0} active + ${completedProjects?.length || 0} completed) for company: ${companyName}`);

      const checkPromises = investorProjects.map((project, requestIndex) => {
        return new Promise((resolve) => {
          console.log(`Starting API request ${requestIndex + 1}/${investorProjects.length} for project: ${project.projectName || project.projectId}`);
          
          // Add timeout to prevent hanging requests
          const timeoutId = setTimeout(() => {
            console.warn(`API request timeout for project: ${project.projectName || project.projectId}`);
            resolve({ exists: false, timedOut: true });
          }, 10000); // 10 second timeout

          getInvestorProjectsRequest({
            url: '/transactions/asc820/list-820s/',
            urlIds: [enterpriseCompanyId, investorCompanyId, project.projectId, 'userId'],
            onSuccess: (responseData) => {
              clearTimeout(timeoutId);
              console.log(`API request ${requestIndex + 1} completed successfully for project: ${project.projectName || project.projectId}`);
              
              const match = Array.isArray(responseData)
                ? responseData.find((c) =>
                    c.portfolioCompanyName?.toLowerCase() === companyName.toLowerCase()
                  )
                : null;

              if (match) {
                console.log(`Found match in project: ${project.projectName || project.projectId}`);
                resolve({
                  exists: true,
                  originalPortfolioCompanyId: match.portfolioCompanyId || match.companyId || null,
                  originalProjectId: project.projectId,
                  originalInvestorCompanyId: project.investorCompanyId,
                  originalTransactionId: match.transactionId || null,
                  projectCreatedDate: project.createdDate || null,
                  transactionDate: match.transactionDate || null,
                  valuationDate: match.valuationDate || null,
                  project,
                  transaction: match,
                  projectName: project.projectName || `Project ${project.projectId}`,
                  projectStatus: project.status || 'Unknown',
                  projectColumn: project.column || 'Unknown',
                });
              } else {
                console.log(`No match found in project: ${project.projectName || project.projectId}`);
                resolve({ exists: false });
              }
            },
            onError: (error) => {
              clearTimeout(timeoutId);
              console.error(`API request ${requestIndex + 1} failed for project: ${project.projectName || project.projectId}`, error);
              resolve({ exists: false, error: true });
            },
          });
        });
      });

      try {
        const results = await Promise.all(checkPromises);
        console.log('All API requests completed. Results:', results.map(r => ({ exists: r.exists, timedOut: r.timedOut, error: r.error })));
        
        const matches = results.filter((res) => res.exists);

        let selectedMatch = null;
        if (matches.length > 0) {
          // Sort matches to find the latest one
          matches.sort((a, b) => {
            // Helper function to get a comparable date value
            const getComparableDate = (match) => {
              // Try to use the most recent date available, fallback to older dates if newer ones are missing
              const dates = [
                match.transactionDate,
                match.valuationDate,
                match.projectCreatedDate,
              ].filter(Boolean); // Remove null/undefined values
              
              if (dates.length === 0) {
                // If no dates available, treat as very old (year 1900)
                return new Date('1900-01-01');
              }
              
              // Convert to Date objects and find the most recent
              const dateObjects = dates.map(dateStr => {
                const date = new Date(dateStr);
                return Number.isNaN(date.getTime()) ? new Date('1900-01-01') : date;
              });
              
              return Math.max(...dateObjects);
            };

            const dateA = getComparableDate(a);
            const dateB = getComparableDate(b);
            
            // Sort in descending order (most recent first)
            return dateB - dateA;
          });

          // Select the first (latest) match
          selectedMatch = matches[0];
          
          // Console log for testing and debugging
          if (matches.length > 1) {
            console.log(`Found ${matches.length} matching companies for "${companyName}":`, 
              matches.map(m => ({
                project: m.projectName,
                status: `${m.projectColumn}${m.projectStatus ? ` - ${m.projectStatus}` : ''}`,
                transactionDate: m.transactionDate,
                valuationDate: m.valuationDate,
                projectCreatedDate: m.projectCreatedDate
              }))
            );
            console.log('Selected latest match:', {
              project: selectedMatch.projectName,
              status: `${selectedMatch.projectColumn}${selectedMatch.projectStatus ? ` - ${selectedMatch.projectStatus}` : ''}`,
              transactionDate: selectedMatch.transactionDate,
              valuationDate: selectedMatch.valuationDate,
              projectCreatedDate: selectedMatch.projectCreatedDate
            });
          } else if (matches.length === 1) {
            console.log(`Found 1 matching company for "${companyName}":`, {
              project: selectedMatch.projectName,
              status: `${selectedMatch.projectColumn}${selectedMatch.projectStatus ? ` - ${selectedMatch.projectStatus}` : ''}`,
              transactionDate: selectedMatch.transactionDate,
              valuationDate: selectedMatch.valuationDate,
              projectCreatedDate: selectedMatch.projectCreatedDate
            });
          }
        } else {
          console.log(`No matching companies found for "${companyName}"`);
        }

        const updatedCompany = {
          ...company,
          existsElsewhere: Boolean(selectedMatch),
          clone: selectedMatch ? company.clone : false,
          originalPortfolioCompanyId: selectedMatch?.originalPortfolioCompanyId || null,
          originalProjectId: selectedMatch?.originalProjectId || null,
          originalInvestorCompanyId: selectedMatch?.originalInvestorCompanyId || null,
          originalTransactionId: selectedMatch?.originalTransactionId || null,
        };

        setExistsInOtherProjects(Boolean(selectedMatch));
        setSelectedMatchDetails(selectedMatch);
        updateCompany(updatedCompany);
      } catch (promiseError) {
        console.error('Error in Promise.all resolution:', promiseError);
        // Reset states on Promise.all error
        setExistsInOtherProjects(false);
        setSelectedMatchDetails(null);
      }
    } catch (error) {
      console.error('Error checking for existing companies:', error);
      // Reset states on error
      setExistsInOtherProjects(false);
      setSelectedMatchDetails(null);
    } finally {
      // Always reset loading state
      setCheckingClone(false);
    }
  };



  const handleNoteChange = (e) => {
    const { value } = e.target;
    const updatedCompany = {
      ...company,
      note: value,
      noteCreatedDatetime: moment().format('YYYY-MM-DD HH:mm:ss'),
      noteAuthor: investor ? `${getUserName()} (Investor)` : getUserName(),
    };
    setNote(value);
    updateCompany(updatedCompany);
  };

  const removeBlock = () => {
    setTimeout(() => {
      const updatedCompanies = projectCompanies.filter((comp) => comp.companyId !== company.companyId);
      setProjectCompanies(updatedCompanies);
    }, 300);
    if (companyNameErrorList[index] === true) {
      const copyNameErrorList = [...companyNameErrorList];
      copyNameErrorList[index] = false;
      setCompanyNameErrorList(copyNameErrorList);
    }
  };

  return (
    <AnimateHeight duration={300} height={projectCompanies[index] ? 'auto' : 0}>
      <div className="company-block">
        <div className="company-input">
          {portfolioCompaniesNames.length ? (
            <Autocomplete
              freeSolo
              options={portfolioCompaniesNames}
              getOptionLabel={(option) => option}
              onInputChange={(event, value) => handleCompanyNameChange(value)}
              value={companyName || ''}
              getOptionDisabled={(option) => {
                const chosenNames = projectCompanies.map((comp) => comp.companyName);
                const isDisabled = chosenNames.includes(option) || companiesInProject.includes(option);
                return isDisabled;
              }}
              className="add-company-block name"
              renderInput={(params) => {
                const { InputProps: autoInputProps, inputProps: nativeInputProps, ...restParams } = params;
                return (
                <TextField
                  placeholder="Company name"
                  {...restParams}
                  value={companyName || ''}
                  inputRef={companyListRef}
                  onBlur={handleCompanyNameBlur}
                  InputProps={{
                    ...autoInputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">{index + 1}.</InputAdornment>
                        {autoInputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {autoInputProps.endAdornment}
                      </>
                    ),
                    inputProps: nativeInputProps,
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                    if (e.key === 'Enter' && index === projectCompanies.length - 1) {
                      const newCompany = {
                        companyId: uuidv4(),
                        companyName: '',
                        capIqIdentifier: '',
                        note: '',
                        noteCreatedDatetime: moment().format('YYYY-MM-DD HH:mm:ss'),
                        noteAuthor: getUserName(),
                        clone: false,
                        existsElsewhere: false,
                        originalPortfolioCompanyId: null,
                        originalProjectId: null,
                        originalInvestorCompanyId: null,
                        originalTransactionId: null
                      };
                      setProjectCompanies([...projectCompanies, newCompany]);
                      setCompanyRowAdded(true);
                    }
                  }}
                  error={companyNameErrorList[index]}
                  helperText={companyNameErrorList[index] ? (
                    <>
                      <ErrorIcon />
                      Please enter company name
                    </>
                  ) : ' '}
                />
                );
              }}
            />
          ) : (
            <TextField
              className="add-company-block name"
              placeholder="Company name"
              value={companyName || ''}
              onChange={(e) => handleCompanyNameChange(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">{index + 1}</InputAdornment>,
              }}
              error={companyNameErrorList[index]}
              helperText={companyNameErrorList[index] ? (
                <>
                  <ErrorIcon />
                  Please enter company name
                </>
              ) : ' '}
            />
          )}
          <TextField
            multiline
            maxRows={4}
            className="add-company-block note"
            placeholder="Type a note, for communication & tracking (optional)"
            value={note || ''}
            onChange={handleNoteChange}
            inputProps={{ maxLength: 750 }}
            helperText={(
              <span>{`${note.length}/750`}</span>
            )}
          />
          <div className="remove-company">
            <IconButton
              size="small"
              onClick={() => {
                if (!existsInOtherProjects) return;
                const newState = !isIconActive;
                setIsIconActive(newState);
                const updatedCompany = {
                  ...company,
                  clone: newState,
                };
                updateCompany(updatedCompany);
              }}
              style={{
                color: checkingClone ? '#2196f3' : isIconActive ? '#4caf50' : '#9e9e9e',
                padding: '4px',
                marginRight: '8px',
                visibility: existsInOtherProjects || checkingClone ? 'visible' : 'hidden',
                cursor: existsInOtherProjects && !checkingClone ? 'pointer' : 'default',
              }}
              title={
                checkingClone
                  ? "Checking for existing companies..."
                  : existsInOtherProjects && selectedMatchDetails
                  ? `Clone from: ${selectedMatchDetails.projectName} (${selectedMatchDetails.projectColumn}${selectedMatchDetails.projectStatus ? ` - ${selectedMatchDetails.projectStatus}` : ''})${
                      selectedMatchDetails.transactionDate
                        ? ` (Transaction: ${moment(selectedMatchDetails.transactionDate).format('MM/DD/YYYY')})`
                        : selectedMatchDetails.valuationDate
                        ? ` (Valuation: ${moment(selectedMatchDetails.valuationDate).format('MM/DD/YYYY')})`
                        : selectedMatchDetails.projectCreatedDate
                        ? ` (Created: ${moment(selectedMatchDetails.projectCreatedDate).format('MM/DD/YYYY')})`
                        : ''
                    }${
                      isIconActive ? ' - Currently selected for cloning' : ' - Click to select for cloning'
                    }`
                  : existsInOtherProjects
                  ? "Clone from another project"
                  : ""
              }
              disabled={!existsInOtherProjects || checkingClone}
            >
              {checkingClone ? (
                <CircularProgress size={20} />
              ) : (
                <ControlPointDuplicateIcon fontSize="small" />
              )}
            </IconButton>
            <Button
              className="remove-company-btn"
              onClick={removeBlock}
            >
              <DeleteOutlineIcon />
            </Button>
          </div>
        </div>
      </div>
    </AnimateHeight>
  );
}

AddCompanyBlock.propTypes = {
  projectCompanies: PropTypes.array.isRequired,
  setProjectCompanies: PropTypes.func.isRequired,
  company: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  companyNameErrorList: PropTypes.array.isRequired,
  setCompanyNameErrorList: PropTypes.func.isRequired,
  investor: PropTypes.bool,
  portfolioCompaniesNames: PropTypes.array,
  setCompanyRowAdded: PropTypes.func.isRequired,
  companyListRef: PropTypes.object.isRequired,
  companiesInProject: PropTypes.array,
  investorCompanyId: PropTypes.string.isRequired,
  enterpriseCompanyId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,

};

AddCompanyBlock.defaultProps = {
  portfolioCompaniesNames: [],
  investor: false,
  companiesInProject: [],
};
